import type { ChannelListResponse, ChannelSummary } from '@apm/contracts';
import { config } from './config.js';
import { normalizeLastItems, withLastItem } from './last-items.js';
import type { LastItems } from './last-items.js';

/** 사람이 고른 채널. **`last-context.ts` 의 `channelId` 와 섞지 않는다** — 아래 주석. */
const STORAGE_KEY = 'bloom.channelId';

/**
 * 공개 채널 목록 (`GET /v1/channels`, v9). **무인증**이다 — 플레이리스트와 같은 자격이다.
 *
 * **재생 가능한 항목이 0인 채널은 서버가 이미 뺐다** (절대 규칙 1 의 승계). 화면이 거르는
 * 것이 아니라 응답에 들어오지 않는다 — 눌렀더니 빈 화면인 채널을 리시버에 주지 않는다.
 *
 * **실패를 던지지 않고 빈 배열로 돌려준다.** 그러면 판정이 `empty` 로 떨어져 「편성된
 * 채널이 없습니다」가 뜬다 — 서버가 꺼진 것과 편성이 빈 것을 화면이 구분하지 못하는 셈이지만,
 * **시청자가 할 일은 어느 쪽이든 같다.** 원인은 진단 화면과 서버 로그가 갖는다.
 */
export async function fetchChannels(
  deviceId?: string,
  signal?: AbortSignal,
): Promise<ChannelSummary[]> {
  const base = config.serverUrl.replace(/\/+$/, '');
  // `deviceId` 는 서버가 로깅만 한다. **제거 금지** — 절대 규칙 2 와 같은 자리이고,
  // 개인화가 들어오면 "이 시청자에게 보일 채널 목록" 이 되는 것이 이 파라미터다.
  const query =
    deviceId !== undefined && deviceId !== '' ? `?deviceId=${encodeURIComponent(deviceId)}` : '';
  try {
    const options = signal ? { signal } : {};
    const response = await fetch(`${base}/v1/channels${query}`, options);
    if (!response.ok) return [];
    return ((await response.json()) as ChannelListResponse).items;
  } catch {
    return [];
  }
}

/**
 * **`last-context.ts` 의 `channelId` 와 다른 값이다.** 저쪽은 **Push 알림을 눌러 돌아왔을
 * 때 복원할 컨텍스트**이지 "지금 보고 있는 채널" 이 아니다 (`docs/23` §7.3).
 * 한 키에 합치면 **알림 복귀가 사용자의 채널 선택을 덮어쓴다** — 같은 오리진에 두
 * `deviceId` 가 사는 문제와 같은 종류다 (`BLOOM-확장-지시서.md` 함정 ⑧).
 *
 * 읽기가 실패해도 앱이 멈추지 않는다 (사파리 프라이빗 모드).
 */
export function readSavedChannel(): string | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw !== null && raw !== '' ? raw : null;
  } catch {
    return null;
  }
}

export function writeSavedChannel(channelId: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, channelId);
  } catch {
    // 저장에 실패해도 이번 세션의 재생은 그대로다. 다음 방문에 목록으로 돌아갈 뿐이다.
  }
}

/**
 * 홈쇼핑 채널에서 **마지막으로 보던 항목** (v26, 절대 규칙 70).
 *
 * ★ **`bloom.channelId` 와 한 키로 합치지 않는다.** 위 `readSavedChannel` 이
 * `last-context.ts` 에 대해 적어 둔 그 경고와 같은 종류다 — 수명이 다르다. 채널은
 * **사람이 고를 때** 한 번 쓰이고 항목은 **재생이 넘어갈 때마다** 쓰인다. 합치면
 * 항목을 기억하는 쓰기가 **사람이 고른 채널을 덮는다.**
 *
 * **판정은 여기 없다** — 정규화·상한·`kind` 자물쇠는 `last-items.ts` 의 순수 함수이고
 * 이 둘은 저장소를 만지는 얇은 껍데기다 (`favorite-channels.ts` 와 같은 갈림).
 */
const LAST_ITEMS_KEY = 'bloom.lastItems';

export function readLastItems(): LastItems {
  try {
    const raw = window.localStorage.getItem(LAST_ITEMS_KEY);
    return normalizeLastItems(raw === null ? null : JSON.parse(raw));
  } catch {
    // 저장소가 막혔거나(사파리 프라이빗) JSON 이 망가졌다. 기억이 없는 것으로 본다 —
    // 그러면 편성 첫 항목부터이고, 그것이 v25 까지의 동작 그대로다.
    return {};
  }
}

/**
 * ★ **자물쇠 ①은 여기가 아니라 부르는 자리에 있다** (`WatchScreen` 이 홈쇼핑일 때만
 * 부른다). 이 함수에 `kind` 를 받아 막지 않는 이유는, 그러면 자물쇠 둘이 **같은 모양**이
 * 되어 한 번의 편집으로 함께 사라질 수 있기 때문이다 — 둘째 자물쇠는 순수 함수 쪽에 있다.
 */
export function writeLastItem(channelId: string, productId: string): void {
  const next = withLastItem(readLastItems(), channelId, productId);
  try {
    window.localStorage.setItem(LAST_ITEMS_KEY, JSON.stringify(next));
  } catch {
    // 못 써도 이번 재생은 그대로다. 다음 방문에 첫 항목부터일 뿐이다.
  }
}
